import React, { useEffect, useState } from 'react';

const LogoutAnimation = ({ onComplete }) => {
  const [phase, setPhase] = useState('fadeIn'); // fadeIn, animate, fadeOut

  useEffect(() => {
    // Start animation sequence
    const timer1 = setTimeout(() => {
      setPhase('animate');
    }, 300);

    const timer2 = setTimeout(() => {
      setPhase('fadeOut');
    }, 2000);

    const timer3 = setTimeout(() => {
      onComplete();
    }, 2500);

    return () => {
      clearTimeout(timer1);
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [onComplete]);

  return (
    <div 
      className={`fixed inset-0 z-[9999] flex items-center justify-center bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 transition-opacity duration-500 ${
        phase === 'fadeIn' ? 'opacity-0' : phase === 'fadeOut' ? 'opacity-0' : 'opacity-100'
      }`}
    >
      {/* Animated Background Shapes */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-blue-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-indigo-500 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-blob animation-delay-4000"></div>
      </div>

      {/* Grid Pattern Overlay */}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]"></div>

      {/* Logout Content */}
      <div className="relative z-10 text-center">
        {/* Animated Icon */}
        <div className="mb-8 flex justify-center">
          <div className={`relative transition-all duration-700 ${
            phase === 'animate' ? 'scale-100 rotate-0' : 'scale-0 rotate-180'
          }`}>
            <div className="absolute inset-0 bg-blue-500 rounded-full blur-2xl opacity-50 animate-pulse"></div>
            <div className="relative w-24 h-24 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-full flex items-center justify-center shadow-2xl">
              <svg 
                className="w-12 h-12 text-white transform transition-transform duration-500"
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path 
                  strokeLinecap="round" 
                  strokeLinejoin="round" 
                  strokeWidth={2} 
                  d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Text Animation */}
        <div className={`space-y-4 transition-all duration-700 ${
          phase === 'animate' ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
        }`}>
          <h2 className="text-3xl font-bold text-white mb-2">
            Logging Out
          </h2>
          <p className="text-blue-200 text-lg">
            Thank you for using CMS
          </p>
          
          {/* Loading Dots */}
          <div className="flex justify-center space-x-2 mt-6">
            <div className={`w-2 h-2 bg-blue-400 rounded-full animate-bounce ${
              phase === 'animate' ? 'opacity-100' : 'opacity-0'
            }`} style={{ animationDelay: '0ms' }}></div>
            <div className={`w-2 h-2 bg-blue-400 rounded-full animate-bounce ${
              phase === 'animate' ? 'opacity-100' : 'opacity-0'
            }`} style={{ animationDelay: '150ms' }}></div>
            <div className={`w-2 h-2 bg-blue-400 rounded-full animate-bounce ${
              phase === 'animate' ? 'opacity-100' : 'opacity-0'
            }`} style={{ animationDelay: '300ms' }}></div>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="mt-8 w-64 mx-auto">
          <div className="h-1 bg-white/20 rounded-full overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r from-blue-500 to-indigo-500 rounded-full progress-bar ${
                phase === 'animate' ? 'progress-bar-fill' : ''
              }`}
            ></div>
          </div>
        </div>
      </div>

      {/* Custom Animations */}
      <style>{`
        @keyframes blob {
          0% {
            transform: translate(0px, 0px) scale(1);
          }
          33% {
            transform: translate(30px, -50px) scale(1.1);
          }
          66% {
            transform: translate(-20px, 20px) scale(0.9);
          }
          100% {
            transform: translate(0px, 0px) scale(1);
          }
        }
        .animate-blob {
          animation: blob 7s infinite;
        }
        .animation-delay-2000 {
          animation-delay: 2s;
        }
        .animation-delay-4000 {
          animation-delay: 4s;
        }
        @keyframes bounce {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        .animate-bounce {
          animation: bounce 1s infinite;
        }
        .progress-bar {
          width: 0;
          transition: width 2s ease-in-out;
        }
        .progress-bar-fill {
          width: 100%;
        }
      `}</style>
    </div>
  );
};

export default LogoutAnimation;

